// Avatar layer for Home scroll animation (balloon + face SVG).

import React, { useId, useMemo } from 'react';
import { motion, useTransform, type MotionValue } from 'framer-motion';
import { useSystemWindowSize } from '../../../hooks';
import { getAvatarPalette } from '../../../styles/playerColors';
import {
  sampleStops,
  toLocalClipVh,
} from '../../../utilities/scrollTimelineMath';
import type {
  ScrollAvatarSegment,
  MotionStop,
} from '../../../types/homeScroll';
import {
  FACE_BOX,
  HEAD_MASK,
} from '../../../components/avatars/configs/balloon';
import { getBaseShape } from '@airhead-games/shared/avatar';
import './ScrollAvatar.css';

const AVATAR_SEGMENT_VH = 100;

type AvatarTracks = {
  opacity: MotionStop[];
  centerXVw: MotionStop[];
  centerYVh: MotionStop[];
  balloonSizePx: MotionStop[];
  rotateDeg: MotionStop[];
  faceOpacity: MotionStop[];
  faceOffsetPx: MotionStop[];
  faceScale: MotionStop[];
  faceSquashX: MotionStop[];
  faceRotateDeg: MotionStop[];
};

type BalloonPalette = {
  base: string;
  dark: string;
  light: string;
  rim: string;
  face: string;
};

type BalloonFaceState = {
  opacity: number | MotionValue<number>;
  offsetX: number | MotionValue<number>;
  scale: number | MotionValue<number>;
  squashX: number | MotionValue<number>;
  rotateZ: number | MotionValue<number>;
};

type ScrollAvatarBalloonProps = {
  size: number;
  palette: BalloonPalette;
  face: BalloonFaceState;
  eyeShape?: string;
  mouthShape?: string;
};

const ScrollAvatarBalloon: React.FC<ScrollAvatarBalloonProps> = ({
  size,
  palette,
  face,
  eyeShape = 'ghost',
  mouthShape = 'watermelon',
}) => {
  const uid = useId();
  const eyePath = useMemo(
    () => getBaseShape('eye', eyeShape)?.path ?? '',
    [eyeShape]
  );
  const mouthPath = useMemo(
    () => getBaseShape('mouth', mouthShape)?.path ?? '',
    [mouthShape]
  );

  return (
    <div
      className="home-scroll-avatar__balloon-shell"
      style={{
        width: size,
        height: size,
        ['--balloon-fill' as string]: palette.base,
        ['--balloon-fill-dark' as string]: palette.dark,
        ['--balloon-fill-light' as string]: palette.light,
        ['--balloon-face-fill' as string]: palette.face,
        ['--balloon-rim' as string]: palette.rim,
      }}
    >
      <svg
        viewBox="0 0 240 240"
        width="100%"
        height="100%"
        aria-hidden="true"
        className="home-scroll-avatar__balloon-svg"
      >
        <defs>
          <radialGradient
            id={`home-scroll-avatar-gradient-${uid}`}
            cx="50%"
            cy="50%"
            r="50%"
          >
            <stop offset="0%" stopColor="var(--balloon-fill-light)" />
            <stop offset="40%" stopColor="var(--balloon-fill)" />
            <stop offset="80%" stopColor="var(--balloon-fill-dark)" />
            <stop offset="100%" stopColor="var(--balloon-rim)" />
          </radialGradient>

          <mask id={`home-scroll-avatar-balloon-mask-${uid}`}>
            <path d={HEAD_MASK.balloon} fill="white" />
          </mask>
          <mask id={`home-scroll-avatar-stem-mask-${uid}`}>
            <path d={HEAD_MASK.stem} fill="white" />
          </mask>
        </defs>

        <circle
          cx="112"
          cy="220"
          r="48"
          fill={`url(#home-scroll-avatar-gradient-${uid})`}
          mask={`url(#home-scroll-avatar-stem-mask-${uid})`}
        />
        <circle
          cx="96"
          cy="92"
          r="120"
          fill={`url(#home-scroll-avatar-gradient-${uid})`}
          mask={`url(#home-scroll-avatar-balloon-mask-${uid})`}
        />
      </svg>

      <div className="home-scroll-avatar__face-stage" aria-hidden="true">
        <motion.div
          className="home-scroll-avatar__face-plane"
          style={{
            opacity: face.opacity,
            x: face.offsetX,
            scale: face.scale,
            scaleX: face.squashX,
            rotateZ: face.rotateZ,
          }}
        >
          <svg
            viewBox="0 0 240 240"
            width="100%"
            height="100%"
            className="home-scroll-avatar__face-svg"
          >
            <g
              transform={`
                translate(${FACE_BOX.leftEye.x}, ${FACE_BOX.leftEye.y})
                translate(20, 20)
                translate(-20, -20)
              `}
            >
              <path d={eyePath} fill="var(--balloon-face-fill)" />
            </g>

            <g
              transform={`
                translate(${FACE_BOX.rightEye.x}, ${FACE_BOX.rightEye.y})
                translate(20, 20)
                translate(-20, -20)
              `}
            >
              <g transform="translate(20, 20) scale(-1, 1) translate(-20, -20)">
                <path d={eyePath} fill="var(--balloon-face-fill)" />
              </g>
            </g>

            <g
              transform={`
                translate(${FACE_BOX.mouth.x}, ${FACE_BOX.mouth.y})
                translate(40, 20)
                translate(-40, -20)
              `}
            >
              <path d={mouthPath} fill="var(--balloon-face-fill)" />
            </g>
          </svg>
        </motion.div>
      </div>
    </div>
  );
};

const viewportYVhToTranslateYPx = (
  viewportYVh: number,
  windowHeight: number
): number => ((viewportYVh - 50) / 100) * windowHeight;

const viewportXVwToTranslateXPx = (
  viewportXVw: number,
  windowWidth: number
): number => ((viewportXVw - 50) / 100) * windowWidth;

const balloonSizePxToScale = (
  balloonSizePx: number,
  avatarBaseSize: number
): number => balloonSizePx / Math.max(avatarBaseSize, 0.0001);

const getViewportCoverBalloonSizePx = (
  windowWidth: number,
  windowHeight: number
): number => Math.hypot(windowWidth, windowHeight) * 1.32;

type ScrollAvatarProps = {
  scrollVh: MotionValue<number>;
} & Omit<ScrollAvatarSegment, 'id'>;

const ScrollAvatar: React.FC<ScrollAvatarProps> = ({
  scrollVh,
  startVh,
  durationVh = 100,
  colorFamily,
  eyeShape,
  mouthShape,
}) => {
  const { width: windowWidth, height: windowHeight } = useSystemWindowSize(50);
  const avatarPalette = useMemo<BalloonPalette>(() => {
    const designPalette = getAvatarPalette(colorFamily);
    return {
      ...designPalette,
      rim: 'var(--avatar-ink)',
      face: 'var(--avatar-ink)',
    };
  }, [colorFamily]);
  const avatarBaseSize =
    windowWidth <= 720 ? 220 : windowWidth <= 1200 ? 320 : 440;
  const balloonCenterOffsetX = (24 / 240) * avatarBaseSize;
  const balloonCenterOffsetY = (28 / 240) * avatarBaseSize;
  const viewportCoverBalloonSizePx = getViewportCoverBalloonSizePx(
    windowWidth,
    windowHeight
  );
  const tracks = useMemo<AvatarTracks>(() => {
    return {
      opacity: [
        [0, 1],
        [75, 1],
        [100, 0, 'easeOut'],
      ],
      centerXVw: [
        [0, 60],
        [20, 60, 'easeOut'],
        [50, 30, 'easeOut'],
        [60, 18, 'easeOut'],
        [100, 18, 'easeOut'],
      ],
      centerYVh: [
        [0, 112],
        [50, 46, 'easeOut'],
        [100, 42, 'easeOut'],
      ],
      balloonSizePx: [
        [0, avatarBaseSize * 0.2],
        [10, avatarBaseSize * 0.6, 'easeOut'],
        [20, avatarBaseSize * 0.8, 'easeOut'],
        [40, viewportCoverBalloonSizePx, 'easeOut'],
        [100, viewportCoverBalloonSizePx],
      ],
      rotateDeg: [
        [0, -8],
        [32, -2, 'easeOut'],
        [68, 0, 'easeOut'],
        [100, 0],
      ],
      faceOpacity: [
        [0, 1],
        [52, 1],
        [62, 0, 'easeOut'],
        [100, 0],
      ],
      faceOffsetPx: [
        [0, 0],
        [50, 0],
        [62, 84, 'easeOut'],
        [100, 84],
      ],
      faceScale: [
        [0, 1],
        [56, 1],
        [62, 0.92, 'easeOut'],
        [100, 0.92],
      ],
      faceSquashX: [
        [0, 1],
        [54, 1],
        [62, 0.48, 'easeOut'],
        [100, 0.48],
      ],
      faceRotateDeg: [
        [0, 0],
        [54, 0],
        [62, 5, 'easeOut'],
        [100, 5],
      ],
    };
  }, [avatarBaseSize, viewportCoverBalloonSizePx]);

  const toLocalVh = (value: number): number =>
    toLocalClipVh(value, startVh, durationVh, AVATAR_SEGMENT_VH);

  const avatarOpacity = useTransform(scrollVh, (value) =>
    sampleStops(tracks.opacity, toLocalVh(value))
  );
  const avatarTranslateX = useTransform(scrollVh, (value) =>
    viewportXVwToTranslateXPx(
      sampleStops(tracks.centerXVw, toLocalVh(value)),
      windowWidth
    )
  );
  const avatarTranslateY = useTransform(scrollVh, (value) =>
    viewportYVhToTranslateYPx(
      sampleStops(tracks.centerYVh, toLocalVh(value)),
      windowHeight
    )
  );
  const avatarScale = useTransform(scrollVh, (value) =>
    balloonSizePxToScale(
      sampleStops(tracks.balloonSizePx, toLocalVh(value)),
      avatarBaseSize
    )
  );
  const avatarRotation = useTransform(scrollVh, (value) =>
    sampleStops(tracks.rotateDeg, toLocalVh(value))
  );
  const faceOpacity = useTransform(scrollVh, (value) =>
    sampleStops(tracks.faceOpacity, toLocalVh(value))
  );
  const faceOffsetX = useTransform(scrollVh, (value) =>
    sampleStops(tracks.faceOffsetPx, toLocalVh(value))
  );
  const faceScale = useTransform(scrollVh, (value) =>
    sampleStops(tracks.faceScale, toLocalVh(value))
  );
  const faceSquashX = useTransform(scrollVh, (value) =>
    sampleStops(tracks.faceSquashX, toLocalVh(value))
  );
  const faceRotateZ = useTransform(scrollVh, (value) =>
    sampleStops(tracks.faceRotateDeg, toLocalVh(value))
  );

  return (
    <div className="home-scroll-avatar__layer">
      <motion.div
        className="home-scroll-avatar__motion"
        style={{
          opacity: avatarOpacity,
          x: avatarTranslateX,
          y: avatarTranslateY,
        }}
      >
        <motion.div
          className="home-scroll-avatar__scale"
          style={{
            rotate: avatarRotation,
            scale: avatarScale,
            transformOrigin: '25% 50%',
          }}
        >
          <div
            className="home-scroll-avatar__center"
            style={{
              transform: `translate(${balloonCenterOffsetX}px, ${balloonCenterOffsetY}px)`,
            }}
          >
            <ScrollAvatarBalloon
              size={avatarBaseSize}
              palette={avatarPalette}
              eyeShape={eyeShape}
              mouthShape={mouthShape}
              face={{
                opacity: faceOpacity,
                offsetX: faceOffsetX,
                rotateZ: faceRotateZ,
                scale: faceScale,
                squashX: faceSquashX,
              }}
            />
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ScrollAvatar;
